import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Tab,
  Tabs,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  AvatarGroup,
  Rating,
  Divider,
  Tooltip,
  CircularProgress,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardMedia,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Skeleton
} from '@mui/material';
import {
  School as TrainingIcon,
  PlayCircle as StartIcon,
  CheckCircle as CompleteIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  CardMembership as CertificateIcon,
  Group as GroupIcon,
  TrendingUp as ProgressIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Timer as TimerIcon,
  Star as StarIcon,
  AssignmentTurnedIn as AssessmentIcon,
  Book as CourseIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  Bookmark as BookmarkIcon,
  Note as NoteIcon,
  Feedback as FeedbackIcon,
  EmojiEvents as BadgeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`training-tabpanel-${index}`}
      aria-labelledby={`training-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TrainingPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>(null);
  const [availableTraining, setAvailableTraining] = useState<any[]>([]);
  const [selectedTraining, setSelectedTraining] = useState<any>(null);
  const [selectedProgress, setSelectedProgress] = useState<any>(null);
  
  // Dialog states
  const [trainingDialog, setTrainingDialog] = useState(false);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [progressDialog, setProgressDialog] = useState(false);
  const [quizDialog, setQuizDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  
  // Quiz state
  const [currentModule, setCurrentModule] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<any>({});
  const [sessionStart, setSessionStart] = useState<Date | null>(null);
  
  // Form states
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    comments: '',
    suggestions: '',
    wouldRecommend: true
  });

  useEffect(() => {
    fetchDashboard();
    fetchAvailableTraining();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/training/user/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTraining = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/training/available`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableTraining(response.data);
    } catch (error) {
      console.error('Error fetching available training:', error);
    }
  };

  const handleEnrollTraining = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/training/${selectedTraining._id}/enroll`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEnrollDialog(false);
      fetchDashboard();
      alert('Successfully enrolled in training!');
    } catch (error) {
      console.error('Error enrolling in training:', error);
      alert('Error enrolling in training');
    }
  };

  const handleStartTraining = async (progressId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/training/progress/${progressId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedProgress(response.data);
      setCurrentModule(0);
      setCurrentLesson(0);
      setSessionStart(new Date());
      setProgressDialog(true);
    } catch (error) {
      console.error('Error starting training:', error);
    }
  };

  const handleCompleteLesson = async () => {
    if (!selectedProgress || !sessionStart) return;
    
    const timeSpent = Math.round((Date.now() - sessionStart.getTime()) / (1000 * 60));
    const module = selectedProgress.training.content.modules[currentModule];
    const lesson = module.lessons[currentLesson];
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/training/progress/${selectedProgress._id}/activity`,
        {
          type: 'complete_lesson',
          moduleId: module.moduleId,
          lessonId: lesson.lessonId,
          data: { timeSpent }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Move to next lesson or module
      if (currentLesson < module.lessons.length - 1) {
        setCurrentLesson(currentLesson + 1);
      } else if (currentModule < selectedProgress.training.content.modules.length - 1) {
        // Check if module has quiz
        if (module.quiz?.enabled) {
          setQuizDialog(true);
        } else {
          setCurrentModule(currentModule + 1);
          setCurrentLesson(0);
        }
      } else {
        // Training completed
        alert('Congratulations! You have completed the training.');
        setProgressDialog(false);
        fetchDashboard();
      }
      
      setSessionStart(new Date());
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!selectedProgress) return;
    
    const module = selectedProgress.training.content.modules[currentModule];
    const answers = Object.entries(quizAnswers).map(([questionId, answer]) => ({
      questionId,
      answer,
      correct: false, // Will be determined by backend
      points: 1
    }));
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/training/progress/${selectedProgress._id}/quiz`,
        {
          moduleId: module.moduleId,
          answers,
          timeSpent: 10 // Placeholder
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQuizDialog(false);
      setQuizAnswers({});
      
      // Move to next module
      if (currentModule < selectedProgress.training.content.modules.length - 1) {
        setCurrentModule(currentModule + 1);
        setCurrentLesson(0);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
  };

  const handleSubmitFeedback = async (progressId: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/training/progress/${progressId}/feedback`,
        feedbackForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFeedbackDialog(false);
      alert('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: any = {
      'Onboarding': 'primary',
      'Safety': 'error',
      'Customer Service': 'success',
      'Operations': 'info',
      'Software': 'secondary',
      'Compliance': 'warning'
    };
    return colors[category] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'completed': 'success',
      'in_progress': 'info',
      'enrolled': 'warning',
      'overdue': 'error'
    };
    return colors[status] || 'default';
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const renderTrainingCard = (training: any, progress?: any) => (
    <Card key={training._id}>
      {training.image && (
        <CardMedia
          component="img"
          height="140"
          image={training.image}
          alt={training.title}
        />
      )}
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Chip
            label={training.category}
            size="small"
            color={getCategoryColor(training.category)}
          />
          <Chip
            label={training.level}
            size="small"
            variant="outlined"
          />
        </Box>
        
        <Typography variant="h6" gutterBottom>
          {training.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {training.description}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimerIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">
              {formatDuration(training.duration?.estimated || 0)}
            </Typography>
          </Box>
          
          {training.certification?.available && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CertificateIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">Certificate</Typography>
            </Box>
          )}
        </Box>
        
        {progress && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Progress</Typography>
              <Typography variant="body2">{progress.progress.overallProgress}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.progress.overallProgress}
              color={getStatusColor(progress.status)}
            />
            {progress.enrollment.dueDate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Due: {dayjs(progress.enrollment.dueDate).format('MMM D, YYYY')}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
      
      <CardActions>
        {progress ? (
          <>
            <Button
              size="small"
              startIcon={<StartIcon />}
              onClick={() => handleStartTraining(progress._id)}
            >
              Continue
            </Button>
            {progress.status === 'completed' && !progress.feedback.submitted && (
              <Button
                size="small"
                startIcon={<FeedbackIcon />}
                onClick={() => {
                  setSelectedProgress(progress);
                  setFeedbackDialog(true);
                }}
              >
                Feedback
              </Button>
            )}
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setSelectedTraining(training);
              setEnrollDialog(true);
            }}
          >
            Enroll
          </Button>
        )}
      </CardActions>
    </Card>
  );

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Skeleton variant="rectangular" height={200} />
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={150} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Training & Certification
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete training modules and earn certifications
        </Typography>
      </Box>

      {/* Alerts */}
      {dashboard?.categorized?.overdue?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have {dashboard.categorized.overdue.length} overdue training assignments
        </Alert>
      )}

      {dashboard?.certifications?.expiring?.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {dashboard.certifications.expiring.length} certification(s) expiring soon
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Completed',
            dashboard?.stats?.completed || 0,
            'Training modules',
            <CompleteIcon color="success" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'In Progress',
            dashboard?.stats?.inProgress || 0,
            'Active training',
            <ProgressIcon color="info" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Certifications',
            dashboard?.stats?.certifications || 0,
            'Active certificates',
            <CertificateIcon color="primary" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total Hours',
            dashboard?.stats?.totalHours || 0,
            'Learning time',
            <TimerIcon color="secondary" />
          )}
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="My Training" icon={<TrainingIcon />} iconPosition="start" />
          <Tab label="Available" icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label="Certifications" icon={<CertificateIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* My Training Tab */}
          <Grid container spacing={3}>
            {/* In Progress */}
            {dashboard?.categorized?.inProgress?.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    In Progress
                  </Typography>
                </Grid>
                {dashboard.categorized.inProgress.map((item: any) => (
                  <Grid item xs={12} md={6} lg={4} key={item._id}>
                    {renderTrainingCard(item.training, item)}
                  </Grid>
                ))}
              </>
            )}

            {/* Assigned */}
            {dashboard?.categorized?.assigned?.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Assigned Training
                  </Typography>
                </Grid>
                {dashboard.categorized.assigned.map((item: any) => (
                  <Grid item xs={12} md={6} lg={4} key={item._id}>
                    {renderTrainingCard(item.training, item)}
                  </Grid>
                ))}
              </>
            )}

            {/* Completed */}
            {dashboard?.categorized?.completed?.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Completed
                  </Typography>
                </Grid>
                {dashboard.categorized.completed.map((item: any) => (
                  <Grid item xs={12} md={6} lg={4} key={item._id}>
                    {renderTrainingCard(item.training, item)}
                  </Grid>
                ))}
              </>
            )}

            {(!dashboard?.categorized?.inProgress?.length && 
              !dashboard?.categorized?.assigned?.length && 
              !dashboard?.categorized?.completed?.length) && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No training enrolled yet
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setTabValue(1)}
                    sx={{ mt: 2 }}
                  >
                    Browse Available Training
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Available Training Tab */}
          <Grid container spacing={3}>
            {availableTraining.map((training) => (
              <Grid item xs={12} md={6} lg={4} key={training._id}>
                {renderTrainingCard(training)}
              </Grid>
            ))}
            
            {availableTraining.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No available training at this time
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Certifications Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Active Certifications
              </Typography>
            </Grid>
            
            {dashboard?.certifications?.active?.map((cert: any) => (
              <Grid item xs={12} md={6} key={cert._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BadgeIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6">
                          {cert.training.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Earned: {dayjs(cert.certification.earnedAt).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        Certificate ID: {cert.certification.certificateId}
                      </Typography>
                      {cert.certification.expiresAt && (
                        <Chip
                          label={`Expires: ${dayjs(cert.certification.expiresAt).format('MMM D, YYYY')}`}
                          size="small"
                          color={
                            dashboard.certifications.expiring.some((e: any) => e._id === cert._id)
                              ? 'warning'
                              : 'default'
                          }
                        />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" startIcon={<DownloadIcon />}>
                      Download Certificate
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            
            {dashboard?.certifications?.active?.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No certifications earned yet
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>

      {/* Enroll Dialog */}
      <Dialog open={enrollDialog} onClose={() => setEnrollDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enroll in Training</DialogTitle>
        <DialogContent>
          {selectedTraining && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedTraining.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedTraining.description}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <TimerIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Duration"
                    secondary={formatDuration(selectedTraining.duration?.estimated || 0)}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Format"
                    secondary={selectedTraining.requirements?.accessibility?.selfPaced ? 'Self-paced' : 'Scheduled'}
                  />
                </ListItem>
                
                {selectedTraining.certification?.available && (
                  <ListItem>
                    <ListItemIcon>
                      <CertificateIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Certification"
                      secondary="Earn a certificate upon completion"
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialog(false)}>Cancel</Button>
          <Button onClick={handleEnrollTraining} variant="contained">
            Enroll Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Training Progress Dialog */}
      <Dialog
        open={progressDialog}
        onClose={() => setProgressDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProgress?.training?.title}
        </DialogTitle>
        <DialogContent>
          {selectedProgress && (
            <>
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={selectedProgress.progress.overallProgress}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Overall Progress: {selectedProgress.progress.overallProgress}%
                </Typography>
              </Box>

              <Stepper activeStep={currentModule} orientation="vertical">
                {selectedProgress.training.content.modules.map((module: any, index: number) => (
                  <Step key={module.moduleId}>
                    <StepLabel>{module.title}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" paragraph>
                        {module.description}
                      </Typography>
                      
                      {currentModule === index && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            {module.lessons[currentLesson]?.title}
                          </Typography>
                          
                          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body1" paragraph>
                              {module.lessons[currentLesson]?.content}
                            </Typography>
                            
                            {/* Resources */}
                            {module.lessons[currentLesson]?.resources?.map((resource: any, idx: number) => (
                              <Chip
                                key={idx}
                                icon={
                                  resource.type === 'video' ? <VideoIcon /> :
                                  resource.type === 'pdf' ? <DocumentIcon /> :
                                  <LinkIcon />
                                }
                                label={resource.title}
                                onClick={() => window.open(resource.url, '_blank')}
                                sx={{ mr: 1, mb: 1 }}
                              />
                            ))}
                          </Box>
                          
                          <Box sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              onClick={handleCompleteLesson}
                              sx={{ mt: 1, mr: 1 }}
                            >
                              Complete Lesson
                            </Button>
                          </Box>
                        </>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={quizDialog} onClose={() => setQuizDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Module Quiz</DialogTitle>
        <DialogContent>
          {selectedProgress && currentModule < selectedProgress.training.content.modules.length && (
            <>
              {selectedProgress.training.content.modules[currentModule].quiz?.questions.map((question: any, index: number) => (
                <Box key={question.questionId} sx={{ mb: 3 }}>
                  <Typography variant="body1" gutterBottom>
                    {index + 1}. {question.question}
                  </Typography>
                  
                  {question.type === 'multiple_choice' && (
                    <RadioGroup
                      value={quizAnswers[question.questionId] || ''}
                      onChange={(e) => setQuizAnswers({
                        ...quizAnswers,
                        [question.questionId]: e.target.value
                      })}
                    >
                      {question.options.map((option: string, idx: number) => (
                        <FormControlLabel
                          key={idx}
                          value={option}
                          control={<Radio />}
                          label={option}
                        />
                      ))}
                    </RadioGroup>
                  )}
                  
                  {question.type === 'true_false' && (
                    <RadioGroup
                      value={quizAnswers[question.questionId] || ''}
                      onChange={(e) => setQuizAnswers({
                        ...quizAnswers,
                        [question.questionId]: e.target.value
                      })}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="True" />
                      <FormControlLabel value="false" control={<Radio />} label="False" />
                    </RadioGroup>
                  )}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitQuiz} variant="contained">
            Submit Quiz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialog} onClose={() => setFeedbackDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Training Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography component="legend">Overall Rating</Typography>
            <Rating
              value={feedbackForm.rating}
              onChange={(e, value) => setFeedbackForm({ ...feedbackForm, rating: value || 0 })}
              size="large"
            />
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments"
            value={feedbackForm.comments}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, comments: e.target.value })}
            sx={{ mt: 3 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Suggestions for Improvement"
            value={feedbackForm.suggestions}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, suggestions: e.target.value })}
            sx={{ mt: 2 }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={feedbackForm.wouldRecommend}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, wouldRecommend: e.target.checked })}
              />
            }
            label="I would recommend this training to others"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSubmitFeedback(selectedProgress._id)}
            variant="contained"
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingPage;